/**
 * Prompt Generator Utilities
 *
 * This file contains utility functions for generating structured prompts
 * by replacing template variables with their values.
 */

/**
 * Generic type for prompt variable values
 * This allows any object with string keys and any type of values
 */
export interface PromptVariables {
  [key: string]: any;
}

/**
 * Generates a prompt by replacing template variables with their values
 * @param template The template string containing variables in {{varName}} format
 * @param variables Object containing values for the variables in the template
 * @returns The prompt with all variables replaced with their values
 */
export function generatePrompt(template: string, variables: PromptVariables): string {
  // Find all variables in the template (format: {{variableName}})
  const variablePattern = /\{\{([^}]+)\}\}/g;
  let match;
  let result = template;

  // Replace each variable with its corresponding value
  while ((match = variablePattern.exec(template)) !== null) {
    const fullMatch = match[0]; // e.g., {{Data}}
    const variableName = match[1].trim(); // e.g., Data

    // Check if the variable exists in the provided variables object
    if (variables.hasOwnProperty(variableName)) {
      // Convert the value to a string (JSON if it's an object)
      const value =
        typeof variables[variableName] === 'object'
          ? JSON.stringify(variables[variableName], null, 2)
          : String(variables[variableName]);

      // Replace all occurrences of this variable in the result
      result = result.replace(new RegExp(escapeRegExp(fullMatch), 'g'), value);
    }
  }

  return result;
}

/**
 * Helper function to escape special characters in a string for use in a RegExp
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
